import React, { useContext } from "react";
import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { Field, FieldType, FieldValue } from "../../../types/FieldCommon";
import { DisableContext } from "../../../contexts/DisableContextProvider";
import { DateTime } from "luxon";
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField } from "@mui/material";

interface IFieldInputProps {
  field: Field;
  updateFieldValue: (value: FieldValue) => void;
  showLabel?: boolean;
}

export default function FieldInput({ field, updateFieldValue, showLabel = true }: IFieldInputProps) {
  const { disabled } = useContext(DisableContext);
  const label = showLabel ? field.label : undefined;

  const fieldEdit = (field: Field) => {
    switch (field.type) {
      case FieldType.DATETIME:
        return (
          <DateTimePicker
            label={label}
            value={typeof field.value === 'string' ? DateTime.fromISO(field.value) : null}
            onChange={(newValue) => updateFieldValue(newValue?.toISO() ?? null)}
            disabled={disabled}
          />
        );

      case FieldType.DATE:
        return (
          <DatePicker
            label={label}
            value={typeof field.value === 'string' ? DateTime.fromISO(field.value) : null}
            onChange={(newValue) => updateFieldValue(newValue?.toISODate() ?? null)}
            disabled={disabled}
          />
        );

      case FieldType.TIME:
        return (
          <TimePicker
            label={label}
            value={typeof field.value === 'string' ? DateTime.fromISO(field.value) : null}
            onChange={(newValue) => updateFieldValue(newValue?.toISO() ?? null)}
            disabled={disabled}
            slotProps={{
              textField: {
                inputProps: { sx: { p: '8px' } },
              },
            }}
          />
        );

      case FieldType.AUTOCOMPLETE:
        return (
          <Autocomplete
            options={(field.options ?? []).map(String)}
            onChange={(_, value) => updateFieldValue(value)}
            value={field.value !== null ? String(field.value) : ' '}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label={label} />}
          />
        );

      case FieldType.MULTISELECT:
        return (
          <Autocomplete<string, true>
            multiple
            options={(field.options ?? []).map(String)}
            onChange={(_, value) => updateFieldValue(value)}
            value={Array.isArray(field.value) ? field.value.map(String) : []}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label={label} />}
          />
        );

      case FieldType.CHECKBOX:
        return (
          <FormControlLabel
            sx={{ width: 'fill-available' }}
            control={
              <Checkbox
                checked={!!field.value}
                onChange={(e) => updateFieldValue(e.target.checked)}
                name={label}
                color="primary"
                disabled={disabled}
              />
            }
            label={label}
          />
        );

      case FieldType.MULTILINE:
        return (
          <TextField
            multiline
            maxRows={10}
            type={field.type}
            id={label}
            label={label}
            onChange={(event) => updateFieldValue(event.target.value)}
            value={field.value ?? ''}
            disabled={disabled}
          />
        );

      default:
        return <TextField type={field.type} id={label} label={label} onChange={(event) => updateFieldValue(event.target.value)} value={field.value ?? ''} disabled={disabled} />;
    }
  };

  return fieldEdit(field);
}